/**
 * Codul de identificare fiscală: curățare și cifră de control.
 *
 * **Partajat între server și browser, dinadins.** Regula are doi consumatori care trebuie
 * să dea același verdict: `libs/be/anaf` o folosește ca să nu iasă pe rețea cu un cod
 * greșit, iar `libs/fe/ui-kit` ca să nu-l lase pe operator să apese butonul degeaba. Două
 * copii ale aceluiași modulo 11 s-ar putea despărți tăcut, iar simptomul ar fi cel mai
 * prost cu putință: componenta spune „cod invalid" pentru un CUI pe care serverul îl
 * acceptă. De aceea stă aici, ca `@pcrm/shared/access` și `@pcrm/shared/icons` — aceleași
 * motive, alt subiect.
 *
 * De ce contează validarea locală: sondat pe viu (2026-08-14), ANAF întoarce pentru un CUI
 * cu cifra de control greșită **exact același răspuns** ca pentru unul inexistent —
 * HTTP 404 cu corp `{"found":[],"notFound":[1234567]}`. Serviciul nu poate spune „ai
 * greșit o cifră". Dacă n-o spunem noi, nu o spune nimeni.
 *
 * Algoritmul e restul modulo 11 cu cheia de mai jos, aliniată la dreapta. Verificat pe
 * 8971726 (Vodafone România): 8·2 + 9·1 + 7·7 + 1·5 + 7·3 + 2·2 = 104 → 1040 mod 11 = 6,
 * adică ultima cifră.
 */
const CONTROL_KEY = [7, 5, 3, 2, 1, 7, 5, 3, 2] as const;

/**
 * Un CUI românesc are între 2 și 10 cifre, ultima fiind cifra de control. Limita de sus
 * vine din cheie: nouă factori acoperă nouă cifre semnificative.
 */
const MIN_DIGITS = 2;
const MAX_DIGITS = CONTROL_KEY.length + 1;

/**
 * Scoate ce pune omul în plus.
 *
 * `RO` e marca de TVA, nu parte din cod — iar operatorul îl copiază de pe factură, unde
 * apare aproape mereu. Spațiile, punctele și liniuțele sunt de tipărire. Toate se curăță
 * *aici*, nu în adaptor: ANAF răspunde **HTTP 400** cu un corp complet diferit dacă
 * primește `"RO8971726"` ca text (sondat 2026-08-14), iar formatul ăla n-are nici
 * `found`, nici `notFound` — adică ar scăpa prin toate ramurile adaptorului.
 */
function strip(raw: string): string {
  return raw
    .trim()
    .toUpperCase()
    .replace(/^RO/, '')
    .replace(/[\s.-]/g, '');
}

/** Cifra de control așteptată pentru partea semnificativă a unui CUI. */
function controlDigit(significant: string): number {
  const digits = [...significant].map(Number);
  const key = CONTROL_KEY.slice(CONTROL_KEY.length - digits.length);

  const sum = digits.reduce((total, digit, index) => total + digit * (key[index] ?? 0), 0);
  const remainder = (sum * 10) % 11;

  // Restul 10 se scrie 0. Fără regula asta, un CUI din zece e respins pe nedrept.
  return remainder === 10 ? 0 : remainder;
}

/**
 * Curăță un CUI scris de om și îl întoarce în forma canonică — **doar cifre, fără `RO` și
 * fără zerouri în față**. `null` dacă nu trece validarea.
 *
 * `null`, nu excepție: aici sunt doi apelanți cu nevoi diferite. Serverul vrea o eroare de
 * domeniu (`InvalidCuiError`, cu ce a tastat omul în mesaj), componenta vrea doar să știe
 * dacă are voie să caute — și n-are ce face cu un `try/catch` la fiecare tastă. Excepția
 * se construiește la capătul care o vrea, nu aici.
 */
export function normalizeCui(raw: string): string | null {
  const cleaned = strip(raw);

  if (!/^[0-9]+$/.test(cleaned)) {
    return null;
  }

  // Zerourile din față nu schimbă codul, dar strică alinierea cheii — `0123456` și
  // `123456` sunt același CUI și trebuie să dea aceeași cifră de control.
  const normalized = cleaned.replace(/^0+/, '');

  if (normalized.length < MIN_DIGITS || normalized.length > MAX_DIGITS) {
    return null;
  }

  const significant = normalized.slice(0, -1);
  const expected = Number(normalized.slice(-1));

  return controlDigit(significant) === expected ? normalized : null;
}
